// @ts-nocheck
import { useRef, useState } from 'react';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import { setDefaultRatings } from 'state/slices/commonSlice';
import { RATING_COLORS } from 'const/Const';
import SwalToast from 'components/Common/SwalToast';
import { API_ENDPOINTS } from '../const/ApiEndpoints';
import useDashBoard from './useDashboard';
import { CoreUtils } from '../utils/Utils';

// a missing rating must stay undefined (the initial state): null would be
// classified as 0 ("low") and would pass `riskValue >= 0` checks
const toRiskValue = (rating) => rating ?? undefined;

/**
 * @function useRiskRating
 * @description a custom hook which handles APIs related to Risk Rating screen
 * @returns {object} object
 */
export default function useRiskRating() {
  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [riskList, setRiskList] = useState([]);
  const [systemRisk, setSystemRisk] = useState([]);
  const [riskValue, setRiskValue] = useState();
  const [riskResult, setRiskResult] = useState([]);
  const [riskResponse, setRiskResponse] = useState({});
  const [riskDasboardData, setRiskDashboardData] = useState([]);
  const [selectedSearchRatings, setSelectedSearchRatings] = useState([]);

  const [riskRatingChartData, setRiskRatingChartData] = useState([]);
  const { userTypes, analystType } = useDashBoard();
  const [riskRatingChartByParameter, setRiskRatingChartByParameter] = useState(
    []
  );
  const [riskRatingMigrationMatrixData, setRiskRatingMigrationMatrixData] =
    useState([]);
  const userData = useSelector((state) => state.common.userData);
  // changed only by resetRiskData (another search opened), so responses of
  // requests started for the previous search can be ignored
  const riskSearchVersion = useRef(0);
  /**
   * @function fetchRiskList
   * @description A function to fetch risk List. Callers fire it from an
   * effect without awaiting it, so request errors are reported here
   * @returns {void}
   */
  const fetchRiskList = (queryParams) => {
    const searchVersion = riskSearchVersion.current;
    setIsLoading(true);
    axios
      .get(`${API_ENDPOINTS.CALCULATE_RISK_RATING}`, {
        params: {
          screen_type: queryParams.screen_type,
          search_id: queryParams.id,
          list_type: 'risk_rating',
        },
      })
      .then((response) => {
        if (searchVersion !== riskSearchVersion.current) {
          return;
        }
        const riskData = response?.data || {};
        const riskRating = toRiskValue(riskData.risk_rating);
        setRiskResponse({
          ...riskData,
          risk_rating: riskRating,
          system_generated_rr: toRiskValue(riskData.system_generated_rr),
        });
        setSystemRisk(riskData.system_generated_rr_values || []);
        setRiskList(riskData.risk_rating_values || []);
        setRiskValue(riskRating);
        setIsLoading(false);
      })
      .catch((err) => {
        if (searchVersion !== riskSearchVersion.current) {
          return;
        }
        setIsLoading(false);
        // errors with an API `Msg` are already shown by the axios interceptor
        if (!err?.response?.data?.Msg) {
          enqueueSnackbar('Error while fetching risk rating', {
            variant: 'error',
          });
        }
      });
  };

  /**
   * @function resetRiskData
   * @description A function to clear risk data of the previously opened search
   * @returns {void}
   */
  const resetRiskData = () => {
    riskSearchVersion.current += 1;
    setRiskList([]);
    setSystemRisk([]);
    setRiskResponse({});
    setRiskValue(undefined);
    setRiskResult([]);
    setIsLoading(false);
  };

  /**
   * @function calculateRiskRating
   * @description A function to calculate risk rating
   * @returns {Promise} returns an axios promise
   */
  const calculateRiskRating = (data) => {
    const searchVersion = riskSearchVersion.current;
    setIsLoading(true);
    return axios
      .post(`${API_ENDPOINTS.CALCULATE_RISK_RATING}`, {
        screen_type: data?.screen_type,
        search_id: data?.search_id,
        risk_rating_values: data?.riskList,
        search_type: data?.search_type,
      })
      .then((response) => {
        if (searchVersion !== riskSearchVersion.current) {
          return;
        }
        const riskRating = toRiskValue(response?.data?.risk_rating);
        setIsLoading(false);
        setRiskValue(riskRating);
        setRiskResult(response?.data?.data);
        setRiskList(response?.data?.risk_rating_values || []);
        setRiskResponse((prev) => ({
          ...prev,
          risk_rating: riskRating,
        }));
        enqueueSnackbar(response?.data?.msg, {
          variant: 'success',
        });
      })
      .catch((err) => {
        if (searchVersion === riskSearchVersion.current) {
          setIsLoading(false);
        }
        throw err;
      });
  };
  const fetchRiskRatingDashboardValue = (data) => {
    const queryParam = {
      ...data,
    };
    setIsLoading(true);
    return axios
      .get(API_ENDPOINTS.RISK_RATING_DASHBOARD, {
        params: queryParam,
      })
      .then(
        (response) => {
          setIsLoading(false);
          setRiskDashboardData(response?.data);
        },
        () => {
          setIsLoading(false);
        }
      );
  };
  const fetchRiskRatingChartdValue = (data) => {
    const queryParam = {
      screen_type: data?.screen_type,
      search_type: data?.search_type !== '' ? data?.search_type : null,
      start_date: data?.start_date !== '' ? data?.start_date : null,
      end_date: data?.end_date !== '' ? data?.end_date : null,
      analyst_id: data?.analyst ? data?.analyst : null,
      manager_id: data?.manager ? data?.manager : null,
      team_id: data?.team ? data?.team : null,
      search_source: data?.search_source || '',
    };
    setIsLoading(true);
    return axios
      .get(API_ENDPOINTS.RISK_RATING_DASHBOARD_CHART, { params: queryParam })
      .then(
        (response) => {
          setIsLoading(false);
          setRiskRatingChartData(response?.data);
        },
        () => {
          setIsLoading(false);
        }
      );
  };
  const fetchRiskRatingChartByParameter = (data) => {
    const queryParam = {
      analyst_id: data?.analyst,
    };
    setIsLoading(true);
    return axios
      .get(API_ENDPOINTS.RISK_RATING_DASHBOARD_CHART_BY_PARAMETER, {
        params: queryParam,
      })
      .then(
        (response) => {
          setIsLoading(false);
          setRiskRatingChartByParameter(response?.data);
        },
        () => {
          setIsLoading(false);
        }
      );
  };
  const fetchMigrationMatrix = (data) => {
    const queryParam = {
      ...data,
    };
    setIsLoading(true);
    return axios
      .get(API_ENDPOINTS.RISK_RATING_DASHBOARD_MIGRATION_MATRIX, {
        params: queryParam,
      })
      .then(
        (response) => {
          setIsLoading(false);
          setRiskRatingMigrationMatrixData(response?.data);
          return response;
        },

        () => {
          setIsLoading(false);
        }
      );
  };
  const getMigrationMatrixData = ({ page = 0, pageSize = 5, filters }) => {
    const modifiedFilters = CoreUtils.buildModifiedFilters(filters);

    const params = {
      pageCount: page + 1,
      pageSize,
      table: true,
      graph: true,
      previous_risk_rating: modifiedFilters?.previous_risk_rating || '',
      current_risk_rating: modifiedFilters?.current_risk_rating || '',
      search_type:
        modifiedFilters?.search_type !== ''
          ? modifiedFilters?.search_type
          : null,
      start_date:
        modifiedFilters?.initiatedStartDate !== ''
          ? modifiedFilters?.initiatedStartDate
          : null,
      end_date:
        modifiedFilters?.initiatedEndDate !== ''
          ? modifiedFilters?.initiatedEndDate
          : null,
      analyst_id: modifiedFilters?.analyst,
      manager_id: modifiedFilters?.manager,
      team_id: modifiedFilters?.team,
      search_source: modifiedFilters?.search_source || '',
    };
    return axios
      .get(API_ENDPOINTS.RISK_RATING_DASHBOARD_MIGRATION_MATRIX, {
        params,
        loader: false,
      })
      .then((res) => {
        setRiskRatingMigrationMatrixData(res.data);
        return {
          data: res.data?.table_data,
          totalRowCount: res.headers?.['x-total-count'],
        };
      });
  };

  /**
   * @function processRiskRatingsData
   * @description A helper function to process and transform risk ratings data
   * @param {Object} data - The raw risk ratings data from API response
   * @returns {Array} Processed and sorted risk ratings data
   */
  const processRiskRatingsData = (data) => {
    const sortedData = _(data)
      .toPairs()
      .map(([label, v]) => ({ min: v.from, max: v.to, label }))
      .sortBy(['min', 'max'])
      .reverse()
      .map((item, index) => {
        const labelLower = item.label.toLowerCase();
        const isLowType =
          labelLower === 'low' || labelLower === 'moderately_low';
        const isMediumType =
          labelLower === 'medium' || labelLower === 'moderately_high';

        let id = index;
        if (isLowType) id = 0;
        else if (isMediumType) id = 1;

        return {
          id,
          isDefault: ['low', 'medium', 'high'].includes(labelLower),
          color: RATING_COLORS[item.label] || '',
          ...item,
        };
      })
      .value();

    return sortedData;
  };

  const getRiskRatings = () => {
    return axios.get(API_ENDPOINTS.RISK_RATING_LEVELS).then((res) => {
      const processedData = processRiskRatingsData(res.data);
      dispatch(setDefaultRatings(processedData));
      return processedData;
    });
  };

  const getRiskRatingsForSearch = ({ searchId, type = 'initial' }) => {
    return axios
      .get(`${API_ENDPOINTS.RISK_RATING_LEVELS}${type}/${searchId}`)
      .then((res) => {
        const processedData = processRiskRatingsData(res.data);
        setSelectedSearchRatings(processedData);
        return processedData;
      });
  };

  const configureRiskRatings = (id, payload) => {
    return axios
      .patch(`${API_ENDPOINTS.RULE_ENGINE_SUPPRESSION_LOGIC}${id}/`, {
        ...payload,
      })
      .then(() => {
        SwalToast({
          icon: 'success',
          title: 'Risk ratings configured successfully',
        });
      });
  };

  return {
    fetchRiskList,
    resetRiskData,
    calculateRiskRating,
    riskList,
    riskValue,
    riskResult,
    setRiskValue,
    setRiskList,
    systemRisk,
    setSystemRisk,
    riskResponse,
    fetchRiskRatingDashboardValue,
    userTypes,
    analystType,
    riskDasboardData,
    riskRatingChartData,
    fetchRiskRatingChartdValue,
    fetchRiskRatingChartByParameter,
    riskRatingChartByParameter,
    fetchMigrationMatrix,
    riskRatingMigrationMatrixData,
    isLoading,
    getMigrationMatrixData,
    getRiskRatings,
    getRiskRatingsForSearch,
    selectedSearchRatings,
    configureRiskRatings,
  };
}
